import React, { ReactNode, memo, useCallback, useEffect } from 'react';
import { Card, CardContent, Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Beer } from '../../state/types';
import { useDevice } from '../../state/providers/DeviceProvider';
import styles from './BeerCardContainer.module.css';

// Global selection state - bypasses React for instant visual feedback
let selectedCardIds: Set<string> = new Set();
const selectionCallbacks: Set<(ids: Set<string>) => void> = new Set();

// Export for action bar to subscribe
export function subscribeToSelection(callback: (ids: Set<string>) => void) {
  selectionCallbacks.add(callback);
  // Immediately call with current state
  callback(new Set(selectedCardIds));
  return () => selectionCallbacks.delete(callback);
}

export function getSelectedCardIds() {
  return new Set(selectedCardIds);
}

export function clearGlobalSelection() {
  selectedCardIds.forEach(id => {
    const card = document.getElementById(`beer-card-${id}`);
    if (card) applySelectionStyles(card, false);
  });
  selectedCardIds.clear();
  notifySelectionChange();
}

export function selectSingleGlobalCard(id: string) {
  selectedCardIds.forEach(existingId => {
    if (existingId === id) {
      return;
    }

    const existingCard = document.getElementById(`beer-card-${existingId}`);
    if (existingCard) {
      applySelectionStyles(existingCard, false);
    }
  });

  selectedCardIds.clear();

  const card = document.getElementById(`beer-card-${id}`);
  if (!card) {
    notifySelectionChange();
    return;
  }

  selectedCardIds.add(id);
  applySelectionStyles(card, true);
  notifySelectionChange();
}

export function toggleGlobalCardSelection(id: string) {
  toggleCardSelection(id);
}

function applySelectionStyles(card: HTMLElement, selected: boolean) {
  // Apply styles directly for maximum speed
  if (selected) {
    card.style.outline = '4px solid var(--primary-colour)';
    card.style.transform = 'scale(1.01)';
    card.style.boxShadow = '0 4px 16px var(--primary-colour-alpha-30)';
  } else {
    card.style.outline = 'none';
    card.style.transform = 'scale(1)';
    card.style.boxShadow = '';
  }
  
  // Left column background stays unchanged (checkmark shows selection)
  
  // Update checkmark and number visibility
  const checkEl = card.querySelector('[data-check]') as HTMLElement;
  const numberEl = card.querySelector('[data-number]') as HTMLElement;
  const leftColumn = card.querySelector('[data-left-column]') as HTMLElement;
  
  if (checkEl) {
    checkEl.style.display = selected ? 'block' : 'none';
    // If beer is in tasting list (yellow background), make checkmark black for contrast
    if (selected && leftColumn) {
      const bgColor = window.getComputedStyle(leftColumn).backgroundColor;
      // Check if background is not transparent (means beer is in tasting list with yellow background)
      const hasYellowBackground = !bgColor.includes('rgba(0, 0, 0, 0)') && !bgColor.includes('transparent');
      checkEl.style.color = hasYellowBackground ? '#000000' : 'var(--primary-colour)';
    }
  }
  if (numberEl) numberEl.style.display = selected ? 'none' : 'block';
}

function toggleCardSelection(id: string) {
  const card = document.getElementById(`beer-card-${id}`);
  if (!card) return;
  
  if (selectedCardIds.has(id)) {
    // Deselect
    selectedCardIds.delete(id);
    applySelectionStyles(card, false);
  } else {
    // Select
    selectedCardIds.add(id);
    applySelectionStyles(card, true);
  }
  
  notifySelectionChange();
}

function notifySelectionChange() {
  // Notify subscribers (action bar) with a copy of the Set
  selectionCallbacks.forEach(cb => cb(new Set(selectedCardIds)));
}

/**
 * Props for the BeerCardContainer component
 */
interface BeerCardContainerProps {
  beer: Beer;
  children?: ReactNode;
  expanded?: boolean;
  statusTag?: ReactNode;
  isTastingListItem?: boolean;
  forceSelected?: boolean; // Override for demo mode during onboarding
  isFirstRenderedBeer?: boolean; // For adding data-tour attributes only to first beer
  badgesInNumberColumn?: ReactNode;
  reserveTitleRightPadding?: boolean;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
}

/**
 * A standardized container for beer cards that ensures consistent structure
 */
const BeerCardContainer: React.FC<BeerCardContainerProps> = ({ 
  beer, 
  children,
  expanded = false,
  statusTag,
  isTastingListItem = false,
  forceSelected = false,
  isFirstRenderedBeer = false,
  badgesInNumberColumn,
  reserveTitleRightPadding = true,
  onTouchStart,
  onTouchMove,
  onTouchEnd
}) => {
  const { isMobile } = useDevice();
  
  // Handle card tap to select/deselect - uses global state for instant feedback
  const handleCardClick = useCallback((e: React.MouseEvent) => {
    // Desktop: read-only mode, no selection
    if (!isMobile) return;
    
    // Don't select if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('[role="button"]') ||
      target.closest('.MuiChip-root') ||
      target.closest('.MuiAccordion-root') ||
      target.closest('.MuiRating-root') ||
      target.closest('[data-tag-filter]') ||
      target.closest('[data-accordion-content]')
    ) {
      return;
    }
    
    // Toggle this card in the multi-select set
    toggleCardSelection(beer.id);
  }, [beer.id, isMobile]);
  
  // Apply forceSelected styles for onboarding demo
  useEffect(() => {
    const card = document.getElementById(`beer-card-${beer.id}`);
    if (card && forceSelected) {
      applySelectionStyles(card, true);
    }
    return () => {
      // Clean up when forceSelected changes or component unmounts
      if (card && forceSelected) {
        applySelectionStyles(card, false);
      }
    };
  }, [beer.id, forceSelected]);
  

  
  return (
    <Card 
      id={`beer-card-${beer.id}`}
      className={`${styles.card} ${expanded ? styles.cardExpanded : ''}`}
      elevation={expanded ? 4 : 2}
      onClick={handleCardClick}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      data-tour={isFirstRenderedBeer ? "beer-card" : undefined}
      sx={{
        cursor: 'pointer',
        backgroundColor: 'var(--secondary-colour)',
        color: '#f5f5f5',
      }}
    >
      {statusTag && (
        <div className={styles.statusTagContainer}>
          {statusTag}
        </div>
      )}
      {/* Left column background - white when selected, orange when in tasting list */}
      <Box
        data-left-column
        data-tour={isFirstRenderedBeer ? "orange-stripe" : undefined}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: '52px',
          backgroundColor: 'transparent',
          borderTopLeftRadius: '8px',
          borderBottomLeftRadius: '8px',
          zIndex: 1,
          transition: 'background-color 0.15s ease',
          overflow: 'hidden',
        }}
      />
      <Box
        data-number-column
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '52px',
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: '100%',
            paddingTop: '15px',
            paddingBottom: isMobile ? '54px' : '13px',
          }}
        >
          <CheckIcon
            data-check
            sx={{
              color: 'var(--primary-colour)',
              fontSize: '2rem',
              zIndex: 2,
              display: 'none',
              pointerEvents: 'none',
            }}
          />
          <Typography
            data-number
            sx={{
              fontWeight: 'bold',
              color: 'var(--primary-colour)',
              fontSize: '1.85rem',
              minWidth: beer.number >= 100 ? '35px' : '24px',
              textAlign: beer.number >= 100 ? 'right' : 'center',
              position: 'relative',
              zIndex: 2,
              lineHeight: 1.2,
              letterSpacing: '-0.01em',
              display: 'block',
              marginRight: beer.number >= 100 ? '4px' : '0px',
              visibility: beer.type === 'cider' ? 'hidden' : 'visible',
            }}
          >
            {beer.number}
          </Typography>

          {badgesInNumberColumn && (
            <Box
              className="BeerBadgesRailSlot BeerBadgesRailSlot--number-column"
              data-badges-rail-slot="number_column"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '6px',
                zIndex: 2,
                pointerEvents: 'auto',
              }}
            >
              {badgesInNumberColumn}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: '20px',
            bottom: isMobile ? '54px' : '13px',
            width: '1px',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            zIndex: 2,
          }}
        />
      </Box>

      <CardContent 
        className={styles.cardContent} 
        sx={{ 
          paddingLeft: '50px',
          paddingBottom: isMobile ? undefined : '0 !important'
        }}
      >
        <Box className={styles.container}>
          {/* Main content column */}
          <Box 
            className={styles.contentColumn}
            sx={{ minHeight: isMobile ? '220px' : 'auto' }}
          >
            <Box className={styles.titleRow}>
              {/* Beer name */}
              <Typography 
                variant="h5" 
                component="h2" 
                className={styles.beerName}
                sx={{
                  fontSize: '1.6rem',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  color: 'var(--primary-colour)',
                  paddingRight: reserveTitleRightPadding ? '108px' : '0px',
                }}
              >
                {beer.name}
              </Typography>
            </Box>
            
            {/* Child content (details, interactions, etc.) */}
            {children}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default memo(BeerCardContainer);
